import dayjs from 'dayjs';
import { createEvent } from 'ics';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type {
  Treatment,
  Customer,
  BookingState,
  Booking,
  SelectedPackage,
  PaymentMethodType,
  Balance,
} from '@/models';
import { STORAGE_KEYS } from '@/services/storageKeys';
import { formatBookingCode } from '@/utils/format';

// Re-export types
export type { Treatment, Customer, BookingState, Booking };

const initialState: Omit<
  BookingState,
  | 'setStoreId'
  | 'setAvailabilityId'
  | 'setNumberOfGuests'
  | 'addCustomer'
  | 'removeCustomer'
  | 'updateCustomer'
  | 'clearAllTreatments'
  | 'setAvailableTreatments'
  | 'setContactInfo'
  | 'setPaymentMethod'
  | 'setUseContactInfoForCustomers'
  | 'setSelectedPackages'
  | 'getMaxDuration'
  | 'getTotalTreatments'
  | 'getStartTime'
  | 'getEndTime'
  | 'setBalanceSummary'
  | 'isStep1Complete'
  | 'isStep2Complete'
  | 'hasMissingTreatments'
  | 'hasAnyTreatments'
  | 'isStep3Complete'
  | 'setBookingCode'
  | 'downloadCalendarEvent'
  | 'reset'
> = {
  storeId: 0, // Default to 0 (no store selected)
  numberOfGuests: 1,
  availabilityId: '',
  customers: [
    {
      name: '',
      telPrefix: '+66',
      tel: '',
      email: '',
      remark: '',
      useContactInfo: false,
      therapistPreference: {
        gender: 9,
        preferredTherapistIds: [],
        unpreferredTherapistIds: [],
      },
      treatmentItems: [],
    },
  ],
  availableTreatments: [],
  name: '',
  telPrefix: '+66',
  tel: '',
  email: '',
  paymentMethod: 0 as PaymentMethodType,
  useContactInfoForCustomers: false,
  selectedPackages: [],
  balance: {
    promotions: [],
    promotionDiscount: 0,
    packageDiscount: 0,
    subtotal: 0,
    total: 0,
    totalDiscount: 0,
    depositTotal: 0,
    depositValue: 0,
    remainingTotal: 0,
  },
  bookingCode: '',
};

export const useBookingStore = create<BookingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStoreId: (id: number) => {
        set({ storeId: id });
      },

      setAvailabilityId: (availabilityId: string) => {
        set({ availabilityId });
      },

      setNumberOfGuests: (count) => {
        const customers: Customer[] = Array.from(
          { length: count },
          (_, index) => {
            const existingCustomer = get().customers[index];
            return (
              existingCustomer || {
                name: '',
                telPrefix: '+66',
                tel: '',
                email: '',
                remark: '',
                useContactInfo: false,
                therapistPreference: {
                  gender: 9, // Any
                  preferredTherapistIds: [],
                  unpreferredTherapistIds: [],
                },
                treatmentItems: [],
              }
            );
          },
        );
        set({ numberOfGuests: count, customers });
      },

      addCustomer: () => {
        const newCustomer: Customer = {
          name: '',
          telPrefix: '+66',
          tel: '',
          email: '',
          remark: '',
          useContactInfo: false,
          therapistPreference: {
            gender: 9, // Any
            preferredTherapistIds: [],
            unpreferredTherapistIds: [],
          },
          treatmentItems: [],
        };
        set((state) => {
          const newCustomers = [...state.customers, newCustomer];
          return {
            customers: newCustomers,
            numberOfGuests: newCustomers.length,
          };
        });
      },

      removeCustomer: (customerIndex: number) => {
        const customers = get().customers.filter(
          (_, index) => index !== customerIndex,
        );
        set({
          customers,
          numberOfGuests: customers.length,
        });
      },

      clearAllTreatments: () => {
        const customers = get().customers.map((customer) => ({
          ...customer,
          treatmentItems: [],
        }));
        set({ customers });
      },

      updateCustomer: (
        customerIndex: number,
        customerData: Partial<Customer>,
      ) => {
        const customers = get().customers.map((customer, index) =>
          index === customerIndex ? { ...customer, ...customerData } : customer,
        );
        set({ customers });
      },

      setAvailableTreatments: (treatments: Treatment[]) => {
        set({ availableTreatments: treatments });
      },

      setContactInfo: (data) => {
        set((state) => ({
          name: data.name ?? state.name,
          telPrefix: data.telPrefix ?? state.telPrefix,
          tel: data.tel ?? state.tel,
          email: data.email ?? state.email,
        }));
      },

      setSelectedPackages: (packages: SelectedPackage[]) => {
        set({ selectedPackages: packages });
      },

      setBalanceSummary: (data: Balance) => {
        set({ balance: data });
      },

      setPaymentMethod: (method: PaymentMethodType) => {
        set({ paymentMethod: method });
      },

      setUseContactInfoForCustomers: (use: boolean) => {
        set({ useContactInfoForCustomers: use });
      },

      setBookingCode: (code: string) => {
        set({ bookingCode: code });
      },

      getMaxDuration: () => {
        const { customers, availableTreatments } = get();

        // Compute total duration per customer (sum of their treatment durations)
        // then return the maximum total among all customers.
        let maxDuration = 0;

        customers.forEach((customer) => {
          let customerTotal = 0;

          customer.treatmentItems.forEach((customerTreatment) => {
            const treatment = availableTreatments.find(
              (t) => t.treatmentId === customerTreatment.treatmentId,
            );
            const option = treatment?.options.find(
              (o) => o.optionId === customerTreatment.optionId,
            );
            if (option) {
              customerTotal += option.duration;
            }
          });

          if (customerTotal > maxDuration) {
            maxDuration = customerTotal;
          }
        });

        return maxDuration;
      },

      getTotalTreatments: () => {
        const { customers } = get();
        return customers.reduce(
          (acc, customer) => acc + customer.treatmentItems.length,
          0,
        );
      },

      getStartTime: () => {
        const { availabilityId } = get();
        if (!availabilityId) return null;
        const startTime = dayjs(availabilityId);
        return startTime.isValid() ? startTime : null;
      },

      getEndTime: () => {
        const { availabilityId } = get();
        if (!availabilityId) return null;
        const startTime = dayjs(availabilityId);
        if (!startTime.isValid()) return null;
        const maxDuration = get().getMaxDuration();
        return startTime.add(maxDuration, 'minute');
      },

      isStep1Complete: () => {
        const state = get();
        return !!(state.storeId > 0 && state.availabilityId);
      },

      isStep2Complete: () => {
        const { customers } = get();
        const hasMissing = get().hasMissingTreatments();
        return customers.length > 0 && !hasMissing;
      },

      // Return true when any customer has no treatment items
      hasMissingTreatments: () => {
        const { customers } = get();
        return customers.some(
          (customer) =>
            !customer.treatmentItems || customer.treatmentItems.length === 0,
        );
      },

      // Return true when any customer has treatment items
      hasAnyTreatments: () => {
        const { customers } = get();
        return customers.some(
          (customer) =>
            customer.treatmentItems && customer.treatmentItems.length > 0,
        );
      },

      isStep3Complete: () => {
        const state = get();
        return !!(state.name && state.email && state.paymentMethod !== 0);
      },

      downloadCalendarEvent: ({ brandName, branchName, translations }) => {
        const state = get();
        const startDateTime = state.getStartTime();
        const endDateTime = state.getEndTime();

        if (!startDateTime || !endDateTime) {
          console.error('Invalid booking date/time');
          return;
        }

        // Build location string
        const location = branchName
          ? `${brandName} - ${branchName}`
          : brandName;

        // Build title
        const title = branchName
          ? `${brandName} (${branchName}) – ${translations.yourAppointment}`
          : `${brandName} – ${translations.yourAppointment}`;

        // Build detailed description with treatment information
        let description = `${translations.bookingCodeLabel} ${formatBookingCode(
          state.bookingCode,
        )}\n`;
        description += `${translations.guests}:\n`;

        // Add treatment details for each customer
        state.customers.forEach((customer, index) => {
          const customerName =
            customer.name || `${translations.customer} ${index + 1}`;

          customer.treatmentItems.forEach((treatment) => {
            const treatmentDetails = state.availableTreatments.find(
              (t) => t.treatmentId === treatment.treatmentId,
            );
            const treatmentName =
              treatmentDetails?.name ||
              translations.treatmentFallback.replace(
                '{{id}}',
                String(treatment.treatmentId),
              );

            description += `    ${index + 1}. ${customerName} – ${treatmentName} ${treatment.optionId} ${translations.minutesShort}\n`;
          });
        });

        // Create ICS event
        const event: Parameters<typeof createEvent>[0] = {
          start: [
            startDateTime.year(),
            startDateTime.month() + 1, // ICS months are 1-indexed
            startDateTime.date(),
            startDateTime.hour(),
            startDateTime.minute(),
          ],
          end: [
            endDateTime.year(),
            endDateTime.month() + 1,
            endDateTime.date(),
            endDateTime.hour(),
            endDateTime.minute(),
          ],
          title: title,
          description: description.trim(),
          location: location,
          status: 'CONFIRMED',
          busyStatus: 'BUSY',
        };

        createEvent(event, (error, value) => {
          if (error) {
            console.error('Error creating calendar event:', error);
            return;
          }

          // Create a blob and download the .ics file
          const blob = new Blob([value], { type: 'text/calendar' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `booking-${formatBookingCode(state.bookingCode)}.ics`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        });
      },

      reset: () => {
        const currentState = get();
        // Reset to initial state but preserve contact information
        set({
          ...initialState,
          name: currentState.name,
          telPrefix: currentState.telPrefix,
          tel: currentState.tel,
          email: currentState.email,
        });
      },
    }),
    {
      name: STORAGE_KEYS.BOOKING_STORE,
      version: 1,
    },
  ),
);
